import { useCallback, useMemo, useState } from 'react';

import { expVal } from '@confluence/feature-experiments';

type UseRemixThinkingBarProgressOptions = {
	onOpenPreview: () => void;
};

type UseRemixThinkingBarProgressResult = {
	showProgressBar: boolean;
	isComplete: boolean;
	onCompleteAnimationFinished: () => void;
	/** Call when the SSE `ACTION_COMPLETE` event fires. */
	signalActionReady: () => void;
	/** Call when a new remix flow starts to clear stale completion state. */
	resetCompletion: () => void;
};

/**
 * Wiring for the remix thinking bar's loading progress bar.
 * When killswitch `maui_thinking_bar_progress_killswitch` is OFF (default), the
 * preview modal opens after the bar fills to 100%. When ON, it opens immediately.
 */
export const useRemixThinkingBarProgress = ({
	onOpenPreview,
}: UseRemixThinkingBarProgressOptions): UseRemixThinkingBarProgressResult => {
	const [isComplete, setIsComplete] = useState(false);

	// Read the killswitch once per hook instance so we don't fire an exposure
	// event on every render of the consumer.
	const isKillswitchOn = useMemo(
		() => expVal<boolean>('maui_thinking_bar_progress_killswitch', 'value', false),
		[],
	);

	const signalActionReady = useCallback(() => {
		if (!isKillswitchOn) {
			// Defer opening the preview until the progress bar fills to 100%.
			setIsComplete(true);
		} else {
			onOpenPreview();
		}
	}, [isKillswitchOn, onOpenPreview]);

	const resetCompletion = useCallback(() => {
		setIsComplete(false);
	}, []);

	return useMemo(
		() => ({
			showProgressBar: !isKillswitchOn,
			isComplete,
			onCompleteAnimationFinished: onOpenPreview,
			signalActionReady,
			resetCompletion,
		}),
		[isKillswitchOn, isComplete, onOpenPreview, signalActionReady, resetCompletion],
	);
};
